import { useCallback, useRef, useState } from "react";

export function useTelemetryCapture({
  captureSessionIdRef,
  captureLabelRef,
  captureTestTypeRef,
  captureDeviceTagRef,
  captureLightingTagRef,
  captureProjectRef,
  captureOperatorRef,
}) {
  const telemetryRowsRef = useRef([]);
  const [telemetryCount, setTelemetryCount] = useState(0);

  const recordFrame = useCallback(
    (row) => {
      if (!row || typeof row !== "object") return;
      const rows = telemetryRowsRef.current;
      const ts = typeof row.ts === "number" ? row.ts : Date.now();
      rows.push({
        ...row,
        ts,
        capturedAtIso: new Date(ts).toISOString(),
        sessionId: captureSessionIdRef.current,
        label: captureLabelRef.current || "",
        testType: captureTestTypeRef.current || "custom",
        deviceTag: captureDeviceTagRef.current || "",
        lightingTag: captureLightingTagRef.current || "",
        project: captureProjectRef.current || "AlphaHubHealthcareSupport",
        operatorTag: captureOperatorRef.current || "",
      });
      if (rows.length > 15000) rows.shift();
      // Keep UI responsive for short remote tests: enable download immediately,
      // then throttle updates to every 5 rows.
      if (rows.length <= 3 || rows.length % 5 === 0) setTelemetryCount(rows.length);
    },
    [
      captureSessionIdRef,
      captureLabelRef,
      captureTestTypeRef,
      captureDeviceTagRef,
      captureLightingTagRef,
      captureProjectRef,
      captureOperatorRef,
    ]
  );

  const recordEvent = useCallback(
    (event, source = "video-calibration") => {
      telemetryRowsRef.current.push({
        ts: Date.now(),
        event,
        sessionId: captureSessionIdRef.current,
        source,
      });
    },
    [captureSessionIdRef]
  );

  const syncTelemetryCount = useCallback(() => {
    setTelemetryCount(telemetryRowsRef.current.length);
  }, []);

  const clearTelemetry = useCallback(() => {
    telemetryRowsRef.current = [];
    setTelemetryCount(0);
  }, []);

  const downloadTelemetryCsv = useCallback(() => {
    const rows = telemetryRowsRef.current;
    if (!rows.length || typeof document === "undefined") return;
    const baseHeaders = [
      "ts",
      "capturedAtIso",
      "sessionId",
      "project",
      "label",
      "testType",
      "operatorTag",
      "deviceTag",
      "lightingTag",
      "winner",
      "winnerScore",
      "yawnDetected",
      "yawnScore",
      "mar",
      "ear",
      "lipCurveSad",
      "handNearFace",
      "handNearMouth",
      "browDown",
      "eyeSquint",
      "mouthFrown",
      "mouthPress",
      "mouthRollLower",
      "mouthStretch",
      "smile",
      "angry",
      "sad",
      "happy",
      "surprise",
      "fear",
      "disgust",
      "stability",
      "diagJson",
    ];
    const dynamicKeySet = new Set();
    const ignoredDynamicKeys = new Set([...baseHeaders, "facBlendshapes", "facJson"]);
    for (const row of rows) {
      for (const [key, value] of Object.entries(row || {})) {
        if (ignoredDynamicKeys.has(key)) continue;
        const type = typeof value;
        if (type === "string" || type === "number" || type === "boolean") {
          dynamicKeySet.add(key);
        }
      }
    }
    const dynamicHeaders = Array.from(dynamicKeySet).sort();
    const facKeySet = new Set();
    for (const row of rows) {
      const fac = row?.facBlendshapes;
      if (!fac || typeof fac !== "object") continue;
      for (const k of Object.keys(fac)) facKeySet.add(k);
    }
    const facKeys = Array.from(facKeySet).sort();
    const facHeaders = facKeys.map((k) => `fac_${k}`);
    const headers = [...baseHeaders, ...dynamicHeaders, ...facHeaders, "fac_json"];
    const escapeCsv = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.join(",")];
    for (const row of rows) {
      const fac = row?.facBlendshapes && typeof row.facBlendshapes === "object" ? row.facBlendshapes : null;
      const baseValues = baseHeaders.map((h) => escapeCsv(row[h]));
      const dynamicValues = dynamicHeaders.map((h) => escapeCsv(row[h]));
      const facValues = facKeys.map((k) => escapeCsv(fac ? fac[k] : ""));
      const facJsonValue = escapeCsv(row?.facJson ?? (fac ? JSON.stringify(fac) : ""));
      lines.push([...baseValues, ...dynamicValues, ...facValues, facJsonValue].join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const stamp = new Date().toISOString().replace(/[:.]/g, "-");
    const a = document.createElement("a");
    a.href = url;
    a.download = `eie-emotion-test-log-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, []);

  return {
    telemetryRowsRef,
    telemetryCount,
    recordFrame,
    recordEvent,
    syncTelemetryCount,
    clearTelemetry,
    downloadTelemetryCsv,
  };
}
